import type { MouseEvent, ReactNode } from "react";
import { useGuestMode } from "@/hooks/useGuestMode";
import { requestTicket } from "@/lib/guest";

interface TicketWallProps {
  /** The real interactive element(s). Rendered as-is for members. */
  children: ReactNode;
  /** What the guest was trying to do, e.g. "save titles". */
  action?: string;
  className?: string;
}

/**
 * Wraps a one-click action. Members get the untouched element; guests see the
 * same element but the click is captured and turned into a seat prompt, so no
 * button in the theatre is ever dead or silently fails.
 */
export const TicketWall = ({ children, action, className }: TicketWallProps) => {
  const { isGuest } = useGuestMode();

  if (!isGuest) return <>{children}</>;

  const intercept = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    requestTicket(action);
  };

  return (
    <span
      className={className}
      onClickCapture={intercept}
      onKeyDownCapture={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          event.stopPropagation();
          requestTicket(action);
        }
      }}
    >
      {children}
    </span>
  );
};

export default TicketWall;
